import Typography from "@material-ui/core/Typography";
import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import AdGroupsCreativesDashboard from "./components/AdGroupsCreativesDashboard/AdGroupsCreativesDashboard";
import BeeswaxReportsTable from "./components/LineItemsReportSectionComponents/BeeswaxReportsTable";
import Button from "../../../components/elements/Button";
import CampaignLineItemsReportDashboard from "./components/LineItemsReportSectionComponents/CampaignLineItemsReportDashboard";
import CampaignReportDashboard from "./components/CampaignReportSectionComponents/CampaignReportDashboard";
import CampaignReportDashboardToolbar from "./components/CampaignReportSectionComponents/CampaignReportDashboardToolbar";
import CampaignTargetingSelectionsDashboard from "./components/CampaignTargetingSelectionsComponents/CampaignTargetingSelectionsDashboard";
import Loader from "../../../components/elements/Loader";
import SiteReportsTable from "./components/LineItemsReportSectionComponents/SiteReportsTable";
import classes from "./CampaignsDashboard.module.scss";
import { BrandgenCampaignPool, BrandgenCampaignPoolReport, ReportTimeFrames } from "../../../types/campaignTypes";
import { SelectOptionType } from "../../../components/elements/Select/Select";
import { getQueryParams } from "../../../helpers/getQueryParams";

type CampaignsDashboardProps = {
  campaignPools: BrandgenCampaignPool[];
  selectedTimeFrame: ReportTimeFrames;
  handleTimeFrameChange: (e: any) => void;
  fetchAndSetCampaigns: () => void;
  handleOpenCampaignsDialog: () => void;
  selectedCampaignPool: BrandgenCampaignPool | null;
  setSelectedCampaignPool: React.Dispatch<React.SetStateAction<BrandgenCampaignPool | null>>;
  selectedCampaignPoolReport: BrandgenCampaignPoolReport | null;
  customReportDate: Date | undefined;
  isFetching: boolean;
  isCustomDateChanged: boolean;
};

const CampaignsDashboard: FC<CampaignsDashboardProps> = ({
  campaignPools,
  selectedTimeFrame,
  handleTimeFrameChange,
  fetchAndSetCampaigns,
  handleOpenCampaignsDialog,
  selectedCampaignPool,
  setSelectedCampaignPool,
  selectedCampaignPoolReport,
  customReportDate,
  isFetching,
  isCustomDateChanged,
}) => {
  const history = useHistory();
  const location = useLocation();

  const campaignOptions: SelectOptionType[] = useMemo(
    () =>
      campaignPools.map(({ name, brandgenCampaignPoolId }) => ({
        label: name || "",
        value: brandgenCampaignPoolId,
      })),
    [campaignPools],
  );

  const handleChangeSelectedCampaign = useCallback(
    (value: SelectOptionType | null) => {
      if (value) {
        const brandgenCampaignPoolId = value.value as string;
        setSelectedCampaignPool(
          campaignPools.find((campaignPool) => campaignPool.brandgenCampaignPoolId === brandgenCampaignPoolId)!,
        );
        history.push({
          pathname: location.pathname,
          search: `?campaignId=${brandgenCampaignPoolId}`,
        });
      }
    },
    [campaignPools, history, location.pathname, setSelectedCampaignPool],
  );

  useEffect(() => {
    const queryParams = getQueryParams();
    const queryCampaignId = queryParams?.campaignId;
    if (!queryCampaignId) {
      setSelectedCampaignPool(campaignPools[0]);
      history.push({
        pathname: location.pathname,
        search: `?campaignId=${campaignPools[0].brandgenCampaignPoolId}`,
      });
    } else {
      const foundCampaign = campaignPools.find(
        (campaignPool) => campaignPool.brandgenCampaignPoolId === queryCampaignId,
      );
      if (foundCampaign) {
        setSelectedCampaignPool(foundCampaign);
      } else {
        setSelectedCampaignPool(campaignPools[0]);
      }
    }
  }, [campaignPools, history, location.pathname, setSelectedCampaignPool]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h2">Campaigns Dashboard</Typography>
        <Button variant="outlined" onClick={handleOpenCampaignsDialog}>
          View All Campaigns
        </Button>
      </div>
      {!selectedCampaignPool ? (
        <Loader />
      ) : (
        <>
          <div className={classes.campaignReportSection}>
            <CampaignReportDashboardToolbar
              selectedTimeFrame={selectedTimeFrame}
              handleTimeFrameChange={handleTimeFrameChange}
              selectedCampaignPool={selectedCampaignPool}
              campaignOptions={campaignOptions}
              handleChangeSelectedCampaign={handleChangeSelectedCampaign}
              fetchAndSetCampaigns={fetchAndSetCampaigns}
            />
            {!isFetching && (
              <CampaignReportDashboard
                campaignPoolReport={selectedCampaignPoolReport}
                campaign={selectedCampaignPool}
                selectedTimeFrame={selectedTimeFrame}
              />
            )}
          </div>

          <div className={classes.campaignsLineItemsReportSection}>
            <CampaignLineItemsReportDashboard
              selectedTimeFrame={selectedTimeFrame}
              campaign={selectedCampaignPool}
              customReportDate={customReportDate}
              isCustomDateChanged={isCustomDateChanged}
            />
          </div>

          <div className={classes.campaignsLineItemsReportSection}>
            <SiteReportsTable selectedTimeFrame={selectedTimeFrame} campaign={selectedCampaignPool} />
          </div>

          <div className={classes.campaignsLineItemsReportSection}>
            <BeeswaxReportsTable selectedTimeFrame={selectedTimeFrame} campaign={selectedCampaignPool} />
          </div>

          <div className={classes.campaignsTargetingInfoSection}>
            <CampaignTargetingSelectionsDashboard campaign={selectedCampaignPool} />
          </div>

          <div className={classes.adGroupsDashboard}>
            <AdGroupsCreativesDashboard
              selectedTimeFrame={selectedTimeFrame}
              selectedCampaign={selectedCampaignPool}
              customReportDate={customReportDate}
              isCustomDateChanged={isCustomDateChanged}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CampaignsDashboard;
